/**
 * PrivacyAnonymizer Service
 * Redacts PII (personally identifiable information) from text
 * - Email addresses
 * - Phone numbers
 * - Physical addresses (basic patterns)
 * - Truncates session IDs
 */

class PrivacyAnonymizer {
  constructor() {
    // Regex patterns for PII detection
    this.patterns = {
      email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      phone: /(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
      // Basic US address pattern (number + street name)
      address: /\b\d{1,5}\s+[A-Za-z0-9\s,.'#-]+\s+(Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Way|Circle|Cir|Place|Pl)\b/gi,
      // Credit card patterns
      creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
      // Social security numbers — dashed, spaced, or undashed (9 straight
      // digits slipped through the dashed-only pattern)
      ssn: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g,
      // ZIP codes (5-digit or ZIP+4) — present in utils/anonymizer.js but
      // this redactor (the one wired into the Mixpanel path) had drifted
      zipCode: /\b\d{5}(?:-\d{4})?\b/g,
      // IP addresses
      ipAddress: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
    };

    // Replacement tokens
    this.replacements = {
      email: '[EMAIL_REDACTED]',
      phone: '[PHONE_REDACTED]',
      address: '[ADDRESS_REDACTED]',
      creditCard: '[CARD_REDACTED]',
      ssn: '[SSN_REDACTED]',
      zipCode: '[ZIP_REDACTED]',
      ipAddress: '[IP_REDACTED]',
    };
  }

  /**
   * Redact PII from a single string
   * @param {string} text - Text to anonymize
   * @param {Object} options - Options for redaction
   * @returns {string} - Anonymized text
   */
  anonymizeText(text, options = {}) {
    if (!text || typeof text !== 'string') {
      return text;
    }

    let anonymized = text;

    // Default: redact everything
    const {
      redactEmails = true,
      redactPhones = true,
      redactAddresses = true,
      redactCreditCards = true,
      redactSSN = true,
      redactIPAddresses = true,
      redactZip = true,
    } = options;

    // Redact in order of specificity (most specific first)
    if (redactSSN) {
      anonymized = anonymized.replace(this.patterns.ssn, this.replacements.ssn);
    }

    if (redactCreditCards) {
      anonymized = anonymized.replace(this.patterns.creditCard, this.replacements.creditCard);
    }

    if (redactEmails) {
      anonymized = anonymized.replace(this.patterns.email, this.replacements.email);
    }

    if (redactPhones) {
      anonymized = anonymized.replace(this.patterns.phone, this.replacements.phone);
    }

    if (redactAddresses) {
      anonymized = anonymized.replace(this.patterns.address, this.replacements.address);
    }

    if (redactIPAddresses) {
      anonymized = anonymized.replace(this.patterns.ipAddress, this.replacements.ipAddress);
    }

    // ZIP last: phone/SSN/card (longer digit runs) are redacted first, so a
    // 5-digit sequence inside them is already consumed and only standalone
    // ZIPs remain to match
    if (redactZip) {
      anonymized = anonymized.replace(this.patterns.zipCode, this.replacements.zipCode);
    }

    // Self-introduced names (EN + ES) — only after an explicit intro phrase,
    // so ordinary capitalized words aren't touched
    anonymized = anonymized.replace(
      /\b(my name is|my name's|i am called|i'm called|me llamo|mi nombre es|me llaman)\s+([A-ZÀ-ÿ][a-zà-ÿ'’-]+(?:\s+[A-ZÀ-ÿ][a-zà-ÿ'’-]+){0,2})/gi,
      '$1 [NAME_REDACTED]'
    );

    return anonymized;
  }

  /**
   * Anonymize an entire message object
   * @param {Object} message - Message object with text, sender, timestamp
   * @returns {Object} - Anonymized message object
   */
  anonymizeMessage(message) {
    if (!message) return message;

    return {
      ...message,
      // This app's messages carry the body in `content` (legacy shapes use
      // `text`) — redact whichever exists, otherwise the spread above copies
      // the raw user text straight into the "anonymized" output
      ...(message.content !== undefined && { content: this.anonymizeText(message.content) }),
      ...(message.text !== undefined && { text: this.anonymizeText(message.text) }),
      // Remove any PII from metadata if exists
      metadata: message.metadata ? this.anonymizeObject(message.metadata) : undefined,
    };
  }

  /**
   * Anonymize array of messages
   * @param {Array} messages - Array of message objects
   * @returns {Array} - Anonymized messages
   */
  anonymizeMessages(messages) {
    if (!Array.isArray(messages)) return messages;
    return messages.map(msg => this.anonymizeMessage(msg));
  }

  /**
   * Anonymize an entire object recursively
   * @param {Object} obj - Object to anonymize
   * @returns {Object} - Anonymized object
   */
  anonymizeObject(obj) {
    if (!obj || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
      return obj.map(item => this.anonymizeObject(item));
    }

    const anonymized = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        anonymized[key] = this.anonymizeText(value);
      } else if (typeof value === 'object') {
        anonymized[key] = this.anonymizeObject(value);
      } else {
        anonymized[key] = value;
      }
    }

    return anonymized;
  }

  /**
   * Truncate session ID to its last 8 characters
   * @param {string} sessionId - Full session ID
   * @returns {string} - Truncated session ID
   */
  truncateSessionId(sessionId) {
    if (!sessionId || typeof sessionId !== 'string') {
      return 'unknown';
    }
    // Take the TAIL, not the head: ids are shaped `session_<ts>_<random>`,
    // so the first 8 chars are the constant prefix "session_" - head
    // truncation collapsed every user into one identity. The tail is the
    // random suffix, which stays unique after truncation.
    return sessionId.slice(-8);
  }

  /**
   * Create anonymous user identifier
   * @param {string} sessionId - Session ID
   * @returns {string} - Anonymous identifier like "User 1a2b3c4d"
   */
  createAnonymousUserId(sessionId) {
    const truncated = this.truncateSessionId(sessionId);
    return `User ${truncated}`;
  }

  /**
   * Anonymize conversation for export/display
   * @param {Object} conversation - Conversation object
   * @returns {Object} - Anonymized conversation
   */
  anonymizeConversation(conversation) {
    if (!conversation) return conversation;

    return {
      id: this.truncateSessionId(conversation.id),
      userId: conversation.sessionId ? this.createAnonymousUserId(conversation.sessionId) : 'Unknown',
      messages: this.anonymizeMessages(conversation.messages),
      startTime: conversation.startTime,
      endTime: conversation.endTime,
      duration: conversation.duration,
      language: conversation.language,
      topic: conversation.topic,
      sentiment: conversation.sentiment,
      resolved: conversation.resolved,
      // Remove any tracking IDs or personal metadata
      metadata: conversation.metadata ? {
        messageCount: conversation.metadata.messageCount,
        platform: conversation.metadata.platform,
        // Exclude any potential PII fields
      } : undefined,
    };
  }

  /**
   * Check if text contains potential PII
   * @param {string} text - Text to check
   * @returns {Object} - Object with boolean flags for each PII type
   */
  detectPII(text) {
    if (!text || typeof text !== 'string') {
      return {
        containsPII: false,
        types: []
      };
    }

    // The shared patterns carry the /g flag, and .test() on a global regex
    // advances lastIndex between calls — without a reset, repeated detectPII
    // calls intermittently miss PII that sits before the stale offset
    const testPattern = (pattern) => {
      pattern.lastIndex = 0;
      const result = pattern.test(text);
      pattern.lastIndex = 0;
      return result;
    };

    const detected = {
      email: testPattern(this.patterns.email),
      phone: testPattern(this.patterns.phone),
      address: testPattern(this.patterns.address),
      creditCard: testPattern(this.patterns.creditCard),
      ssn: testPattern(this.patterns.ssn),
      ipAddress: testPattern(this.patterns.ipAddress),
      zipCode: testPattern(this.patterns.zipCode),
    };

    const types = Object.keys(detected).filter(key => detected[key]);

    return {
      containsPII: types.length > 0,
      types,
      detected
    };
  }

  /**
   * Anonymize analytics event
   * @param {Object} event - Analytics event
   * @returns {Object} - Anonymized event
   */
  anonymizeAnalyticsEvent(event) {
    if (!event) return event;

    return {
      ...event,
      sessionId: this.truncateSessionId(event.sessionId),
      // Redact any text fields
      queryText: event.queryText ? this.anonymizeText(event.queryText) : undefined,
      responseText: event.responseText ? this.anonymizeText(event.responseText) : undefined,
      // Preserve metrics
      timestamp: event.timestamp,
      type: event.type,
      metrics: event.metrics,
    };
  }

  /**
   * Generate privacy report for a dataset
   * @param {Array} data - Array of data items to check
   * @returns {Object} - Privacy report
   */
  generatePrivacyReport(data) {
    if (!Array.isArray(data)) return { clean: true, issues: [] };

    const issues = [];
    
    data.forEach((item, index) => {
      const text = typeof item === 'string' ? item : JSON.stringify(item);
      const piiCheck = this.detectPII(text);
      
      if (piiCheck.containsPII) {
        issues.push({
          index,
          types: piiCheck.types,
          itemType: typeof item,
        });
      }
    });

    return {
      clean: issues.length === 0,
      totalItems: data.length,
      issuesFound: issues.length,
      issues: issues,
      percentage: data.length > 0 ? ((data.length - issues.length) / data.length * 100).toFixed(1) : 100,
    };
  }
}

const privacyAnonymizer = new PrivacyAnonymizer();
export default privacyAnonymizer;

